import { tokenService } from './tokenService';
import { notifyAuthSessionExpired, SESSION_EXPIRED_MESSAGE } from './authSession';

export const AUTH_REFRESH_RETRY_LIMIT = 2;

export class UnauthorizedRetryError extends Error {
  status = 401;

  constructor(message = 'Unauthorized') {
    super(message);
    this.name = 'UnauthorizedRetryError';
  }
}

export const AUTH_REFRESH_FAILED_MESSAGE =
  SESSION_EXPIRED_MESSAGE;

export function isUnauthorizedRetryError(error: unknown): error is UnauthorizedRetryError {
  if (!error || typeof error !== 'object') return false;
  const maybe = error as { status?: unknown; name?: unknown };
  return maybe.status === 401 || maybe.name === 'UnauthorizedRetryError';
}

export function getStoredAccessToken(): string | null {
  const token = tokenService.getToken();
  if (token) return token;
  try {
    return localStorage.getItem('token');
  } catch {
    return null;
  }
}

function getStoredRefreshToken(): string | null {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) return refreshToken;

    const legacyRefreshToken = localStorage.getItem('refresh_token');
    if (legacyRefreshToken) {
      localStorage.setItem('refreshToken', legacyRefreshToken);
      return legacyRefreshToken;
    }
  } catch {
    return null;
  }
  return null;
}

export async function refreshAccessTokenForRetry(): Promise<string | null> {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    console.warn('[authRefresh] No refresh token available for auth retry');
    notifyAuthSessionExpired('missing_refresh_token');
    return null;
  }
  const refreshedToken = await tokenService.refreshToken();
  if (!refreshedToken) {
    notifyAuthSessionExpired('token_refresh_failed');
  }
  return refreshedToken;
}

export async function getAccessTokenForAuthenticatedRequest(): Promise<string | null> {
  const token = getStoredAccessToken();
  if (!token) return null;

  try {
    if (tokenService.isTokenExpired()) {
      return refreshAccessTokenForRetry();
    }
  } catch {
    notifyAuthSessionExpired('token_refresh_failed');
    return null;
  }

  return token;
}
